/**
 * The single source of truth for which languages the app ships.
 *
 * Adding a language means: add it here, add a `locales/<code>/` folder with one
 * JSON file per section, and the loader + parity test pick it up automatically.
 * Nothing else hardcodes the list — `SettingsPage` used to carry two duplicate
 * copies of it and no longer does.
 */

export interface SupportedLanguage {
  /** BCP-47 base code, also the locale folder name. */
  code: string;
  /** Name of the language written in that language (what the picker shows). */
  nativeName: string;
  /** Locale tag used for Intl date/number formatting. */
  intlTag: string;
}

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
  { code: "da", nativeName: "Dansk", intlTag: "da-DK" },
  { code: "de", nativeName: "Deutsch", intlTag: "de-DE" },
  { code: "en", nativeName: "English", intlTag: "en-US" },
  { code: "es", nativeName: "Español", intlTag: "es-ES" },
  { code: "fr", nativeName: "Français", intlTag: "fr-FR" },
  { code: "it", nativeName: "Italiano", intlTag: "it-IT" },
  { code: "nl", nativeName: "Nederlands", intlTag: "nl-NL" },
  { code: "pt", nativeName: "Português", intlTag: "pt-BR" },
  { code: "ja", nativeName: "日本語", intlTag: "ja-JP" },
  { code: "zh", nativeName: "中文 (简体)", intlTag: "zh-CN" },
] as const;

export const SUPPORTED_LANGUAGE_CODES: readonly string[] = SUPPORTED_LANGUAGES.map(l => l.code);

export const DEFAULT_LANGUAGE = "en";

/**
 * Map anything the browser, the backend or a URL might hand us onto a code we
 * actually ship. `pt-BR` -> `pt`, `zh-Hans-CN` -> `zh`, `EN` -> `en`.
 * Returns null when we don't ship the language, so callers can decide whether
 * to fall back or ignore.
 */
export function normalizeLanguage(tag: string | null | undefined): string | null {
  if (!tag) return null;
  // Tolerate the JSON-quoted value written by the legacy useStickyState picker.
  const cleaned = String(tag).trim().replace(/^"+|"+$/g, "");
  if (!cleaned) return null;
  const base = cleaned.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_CODES.includes(base) ? base : null;
}

/** Native name for a code, falling back to the code itself for unknown input. */
export function languageName(code: string): string {
  return SUPPORTED_LANGUAGES.find(l => l.code === code)?.nativeName ?? code;
}

/** Intl locale tag for a code, falling back to English formatting. */
export function intlTagFor(code: string): string {
  return SUPPORTED_LANGUAGES.find(l => l.code === code)?.intlTag ?? "en-US";
}
